import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { ProgramDetails, ProgramIntro, ProgramRow } from "@/components/services/ProgramCard";
import { Reveal } from "@/components/Reveal";
import { JsonLd } from "@/components/JsonLd";
import { FaqAccordion } from "@/components/FaqAccordion";
import { AICitations } from "@/components/AICitations";
import { SERVICES } from "@/content/services";
import { SITE } from "@/content/site";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { buildSeo } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { RangpurAreasStrip } from "@/components/RangpurAreasStrip";
import { RelatedReading } from "@/components/RelatedReading";
import { AiAnswersCallout } from "@/components/AiAnswersCallout";

export const Route = createFileRoute("/services/")({
  head: () => buildSeo({
    title: "SEO Services in Bangladesh — Technical, Local & AEO | Freelancer Tamal",
    description: "Senior SEO services from a local SEO expert in Bangladesh: technical SEO audits, local SEO, Shopify & ecommerce SEO, content strategy, AEO and GEO. Real prices, no ranking guarantees.",
    path: "/services",
    ogEyebrow: "Services",
    image: "/og/services.jpg",
    keywords: "SEO services Bangladesh, SEO consultant Rangpur, technical SEO audit, local SEO services, Shopify SEO, ecommerce SEO, content strategy",
    alternates: { bn: "/bn/services" },
  }),
  component: ServicesIndex,
});

const FAQS = [
  { q: "How much does SEO cost in Bangladesh?", a: "My SEO retainers start at $1,500/month for local businesses and $3,500/month for ecommerce or SaaS. One-off technical audits start at $2,500. Pricing depends on site size, competition, and whether I'm leading strategy only or executing too." },
  { q: "How long does SEO take to show results?", a: "Local SEO usually shows movement in 30–60 days. National or international SEO programs typically deliver meaningful traffic gains in 4–6 months and compound from there. Anyone promising faster is selling you something risky." },
  { q: "Do you guarantee #1 rankings?", a: "No — and you should run from anyone who does. I guarantee a documented process, transparent reporting, and that I'll only take on engagements where I genuinely believe I can move the needle for you." },
  { q: "Do you work with clients outside Bangladesh?", a: "Yes — most of my work is for clients in the US, Germany, Singapore, and the UK. I'm fluent in English and work async on Slack/Linear with weekly strategy calls." },
  { q: "What do your technical SEO services include?", a: "A crawl of the whole site, indexing and canonical checks, Core Web Vitals and speed fixes, structured data, internal linking, and a prioritized fix list your developer can act on — or I implement it directly." },
];

const AEO_RELATED: Record<string, string> = {
  seo: "AEO is the natural extension of classic SEO — see how I optimize for AI answer engines on the dedicated AEO & GEO page.",
  "technical-seo-audit":
    "A clean technical foundation is what makes a site quotable by LLMs. Pair this with my AEO & GEO program for AI search visibility.",
  "content-strategy":
    "Content that gets cited in ChatGPT and Perplexity follows specific structural rules — covered in detail on the AEO & GEO page.",
  "ecommerce-seo":
    "Product and FAQ schema also drive AI shopping answers — see the AEO & GEO program for citation-worthy ecommerce content.",
};

function ServicesIndex() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]} />
      <h1 className="sr-only">SEO Services in Bangladesh</h1>
      <Section
        eyebrow="Services"
        title={<>SEO that <span className="text-gradient">earns its keep.</span></>}
      >
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          Four focused engagements. No bloated retainers, no SEO theater —
          just the work that moves rankings, traffic, and revenue.
        </p>
        <ReviewedBy reviewedOn="2026-06-01" />
        <p className="mt-6 max-w-2xl text-muted-foreground">
          Before comparing quotes, read the <Link to="/seo-services-bangladesh" className="text-primary underline underline-offset-4">SEO services in Bangladesh scope and cost checklist</Link>: what to include, what may cost extra, and what to ask a provider.
        </p>
      </Section>

      <div className="container-x space-y-px bg-border rounded-3xl overflow-hidden border border-border">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug}>
            <div id={s.slug} className="bg-background p-10 lg:p-16 grid lg:grid-cols-12 gap-10 scroll-mt-32">
              <ProgramIntro label={<>0{i + 1}</>} title={s.title} tagline={s.tagline}>
                <p className="mt-4 text-muted-foreground">{s.description}</p>
              </ProgramIntro>
              <ProgramDetails deliverables={s.deliverables} outcomes={s.outcomes}>
                {s.faqs && s.faqs.length > 0 && (
                  <ProgramRow heading="People also ask" spaced>
                    <FaqAccordion items={s.faqs} idPrefix={`faq-${s.slug}`} />
                  </ProgramRow>
                )}
                {s.slug === "aeo-geo" && (
                  <ProgramRow heading="Live proof — recent AI citations" spaced>
                    <AICitations />
                    <div className="mt-6">
                      <Link
                        to="/services/aeo-geo"
                        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium"
                      >
                        See the full AEO &amp; GEO service page <ArrowUpRight size={16} />
                      </Link>
                    </div>
                  </ProgramRow>
                )}
                {AEO_RELATED[s.slug] && (
                  <div className="sm:col-span-2 rounded-2xl border border-border/70 bg-secondary/40 p-5 flex items-start gap-3">
                    <Sparkles size={18} className="text-primary mt-0.5 flex-none" />
                    <p className="text-sm text-muted-foreground">
                      {AEO_RELATED[s.slug]}{" "}
                      <Link
                        to="/services/aeo-geo"
                        className="text-foreground underline underline-offset-4 decoration-primary/60 hover:decoration-primary"
                      >
                        Explore AEO &amp; GEO →
                      </Link>
                    </p>
                  </div>
                )}
              </ProgramDetails>
            </div>
          </Reveal>
        ))}
      </div>

      <Section eyebrow="FAQ" title="Common questions about working with me.">
        <FaqAccordion items={FAQS} idPrefix="faq-general" />
        <div className="mt-12">
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium">
            Tell me about your project <ArrowUpRight size={16} />
          </Link>
        </div>
      </Section>

      <RelatedReading
        eyebrow="Related reading"
        title={<>Pillar guides that shape <span className="text-gradient">how I work.</span></>}
        description="The thinking behind every engagement — from AEO fundamentals to technical audits and content systems."
        hrefs={[
          "/blog/what-is-aeo-how-to-get-cited-by-chatgpt-2026",
          "/blog/technical-seo-audit-guide-2025",
          "/blog/schema-markup-for-aeo-2026",
          "/blog/keyword-research-that-brings-traffic",
          "/blog/state-of-seo-bangladesh-2026",
          "/blog/best-seo-tools-2025",
        ]}
      />

      <RelatedReading
        eyebrow="Pricing & ROI"
        title={<>What this work <span className="text-gradient">actually costs.</span></>}
        description="Transparent pricing benchmarks, package scope, and realistic timelines for SEO in Bangladesh — written before you ever get a quote from me."
        hrefs={[
          "/blog/seo-service-price-in-bangladesh-2026",
          "/blog/monthly-seo-package-bangladesh-2026",
          "/blog/how-long-does-seo-take-bangladesh-2026",
          "/blog/90-day-seo-roadmap-bangladesh-small-business-2026",
          "/blog/local-seo-cost-bangladesh-2026",
        ]}
      />

      <Section className="!pt-0">
        <AiAnswersCallout
          context="SEO, AEO & GEO"
          questionSlugs={[
            "what-is-aeo",
            "aeo-vs-seo-vs-geo",
            "get-cited-by-chatgpt",
            "what-schema-for-aeo",
          ]}
        />
      </Section>

      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Service",
        name: "SEO Consulting",
        serviceType: "Search Engine Optimization",
        areaServed: ["Bangladesh", "Worldwide"],
        provider: {
          "@type": "Person",
          "@id": `${SITE.url}/#person`,
          name: SITE.fullName,
          url: `${SITE.url}/about`,
          jobTitle: "SEO Expert & Digital Marketing Consultant",
          sameAs: Object.values(SITE.social),
        },
        review: {
          "@type": "Review",
          reviewBody: "Page written, fact-checked, and maintained by Freelancer Tamal — SEO consultant focused on technical SEO, local SEO, ecommerce SEO and AEO/GEO.",
          author: {
            "@type": "Person",
            "@id": `${SITE.url}/#person`,
            name: SITE.fullName,
            url: `${SITE.url}/about`,
            jobTitle: "SEO Expert",
          },
          datePublished: new Date().toISOString().slice(0, 10),
        },
      }} />
      <RangpurAreasStrip
        eyebrow="Local SEO · Rangpur"
        title={<>Need SEO in a specific <span className="text-gradient">Rangpur area?</span></>}
        description="Every service on this page is delivered with hyperlocal precision. Pick the bazaar, intersection, or campus you operate near and see the playbook for that exact zone."
      />
    </>
  );
}